/**
 * 智能滤镜链的求值（M4-06 / ADR-0008）。
 *
 * 原始像素→依次应用启用的滤镜→输出，原始像素（`SmartObjectLayer.raster`）
 * 从未被改写——每次都是从原始像素重新算一遍，这正是"非破坏"的字面含义。
 * 合成路径要求同步（见 ADR-0008），所以这里是自带的盒式模糊近似，
 * 不是 `engine/filters` 里那两个 wasm 异步实现。
 */
import type { SmartFilter } from '../document/types';

function num(params: Readonly<Record<string, number | string>>, key: string, fallback: number): number {
  const v = params[key];
  return typeof v === 'number' ? v : fallback;
}

/**
 * 单通道盒式模糊（水平+垂直两趟），三次叠加近似高斯——与
 * `engine/effects/render.ts` 的 `boxBlurAlpha` 同一手法，这里扩到 RGBA 四通道。
 */
function boxBlurPass(src: Uint8Array, width: number, height: number, radius: number): Uint8Array {
  if (radius <= 0) return src;
  const out = new Uint8Array(src.length);
  const horizontal = new Uint8Array(src.length);

  // 水平
  for (let y = 0; y < height; y++) {
    for (let c = 0; c < 4; c++) {
      let sum = 0;
      let count = 0;
      for (let x = -radius; x <= radius; x++) {
        const xi = Math.min(width - 1, Math.max(0, x));
        sum += src[(y * width + xi) * 4 + c]!;
        count++;
      }
      horizontal[(y * width) * 4 + c] = Math.round(sum / count);
      for (let x = 1; x < width; x++) {
        const addX = Math.min(width - 1, x + radius);
        const removeX = Math.max(0, x - radius - 1);
        sum += src[(y * width + addX) * 4 + c]! - src[(y * width + removeX) * 4 + c]!;
        horizontal[(y * width + x) * 4 + c] = Math.round(sum / (2 * radius + 1));
      }
    }
  }

  // 垂直
  for (let x = 0; x < width; x++) {
    for (let c = 0; c < 4; c++) {
      let sum = 0;
      for (let y = -radius; y <= radius; y++) {
        const yi = Math.min(height - 1, Math.max(0, y));
        sum += horizontal[(yi * width + x) * 4 + c]!;
      }
      out[x * 4 + c] = Math.round(sum / (2 * radius + 1));
      for (let y = 1; y < height; y++) {
        const addY = Math.min(height - 1, y + radius);
        const removeY = Math.max(0, y - radius - 1);
        sum += horizontal[(addY * width + x) * 4 + c]! - horizontal[(removeY * width + x) * 4 + c]!;
        out[(y * width + x) * 4 + c] = Math.round(sum / (2 * radius + 1));
      }
    }
  }
  return out;
}

/** 三趟盒式模糊叠加，近似高斯（业界常用手法：3 次盒式模糊已很接近真高斯曲线）。 */
function approximateGaussianBlur(pixels: Uint8Array, width: number, height: number, radius: number): Uint8Array {
  if (radius <= 0) return pixels;
  let out = boxBlurPass(pixels, width, height, radius);
  out = boxBlurPass(out, width, height, radius);
  out = boxBlurPass(out, width, height, radius);
  return out;
}

function applyUnsharp(
  original: Uint8Array,
  blurred: Uint8Array,
  amount: number,
  threshold: number,
): Uint8Array {
  const out = new Uint8Array(original.length);
  for (let i = 0; i < original.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const orig = original[i + c]!;
      const blur = blurred[i + c]!;
      const diff = orig - blur;
      const value = Math.abs(diff) > threshold ? orig + diff * amount : orig;
      out[i + c] = Math.min(255, Math.max(0, Math.round(value)));
    }
    out[i + 3] = original[i + 3]!;
  }
  return out;
}

/**
 * 依次应用滤镜链。禁用的滤镜跳过；未知 kind 视为恒等（前向兼容——将来
 * 加新滤镜种类时，旧版本打开同一文档不该炸，只是那一步不生效）。
 */
export function renderSmartFilterChain(
  pixels: Uint8Array,
  width: number,
  height: number,
  filters: readonly SmartFilter[],
): Uint8Array {
  let current = pixels;
  for (const filter of filters) {
    if (!filter.enabled) continue;
    if (filter.kind === 'gaussianBlur') {
      const radius = Math.max(0, Math.round(num(filter.params, 'radius', 4)));
      current = approximateGaussianBlur(current, width, height, radius);
    } else if (filter.kind === 'unsharpMask') {
      const amount = num(filter.params, 'amount', 0.5);
      const radius = Math.max(0, Math.round(num(filter.params, 'radius', 4)));
      const threshold = num(filter.params, 'threshold', 4);
      const blurred = approximateGaussianBlur(current, width, height, radius);
      current = applyUnsharp(current, blurred, amount, threshold);
    }
    // 其余 kind：恒等，不认识的滤镜不阻塞链条
  }
  return current;
}
